import { stripAngleBrackets } from '@/utils/formatting/strings';

// Zero-width spaces are inserted by highlightAllVariables so the cursor
// lands outside styled <span> elements. They must be ignored when mapping
// between raw title offsets and DOM text positions.
const ZWS = '\u200B';

export const TITLE_VARIABLE_ATTRIBUTE = 'data-slide-title-variable';

/**
 * Converts contenteditable HTML to safe title text. HTML attributes and tags
 * are discarded, script and style contents are removed, and angle brackets
 * cannot persist for another renderer to interpret as markup later.
 */
export function htmlToPlainTextWithLineBreaks(
  html: string,
  allowLineBreaks: boolean
): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  removeNonTextContent(doc);
  const editorText = getDocumentText(doc, allowLineBreaks);

  // Contenteditable encodes angle brackets that a user types as text. Parse
  // the extracted value once more so typed and pasted markup follow the same
  // plain-text policy as markup already present in the editor DOM.
  const textDoc = new DOMParser().parseFromString(editorText, 'text/html');
  removeNonTextContent(textDoc);
  return stripAngleBrackets(getDocumentText(textDoc, allowLineBreaks));
}

function removeNonTextContent(doc: Document): void {
  doc
    .querySelectorAll('script, style, noscript, template')
    .forEach((element) => element.remove());
}

function getDocumentText(doc: Document, allowLineBreaks: boolean): string {
  return allowLineBreaks
    ? serializeNodeList(doc.body.childNodes)
    : (doc.body.textContent ?? '');
}

const BLOCK_TAG_NAMES = new Set(['DIV', 'P']);

function serializeNodeList(nodeList: NodeListOf<ChildNode>): string {
  let output = '';
  const nodes = Array.from(nodeList);
  let index = 0;

  while (index < nodes.length) {
    const node = nodes[index];
    if (!node) break;
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      BLOCK_TAG_NAMES.has((node as HTMLElement).tagName)
    ) {
      const blockTexts: string[] = [];
      while (index < nodes.length) {
        const blockNode = nodes[index];
        if (!blockNode) break;
        if (
          blockNode.nodeType !== Node.ELEMENT_NODE ||
          !BLOCK_TAG_NAMES.has((blockNode as HTMLElement).tagName)
        ) {
          break;
        }
        blockTexts.push(serializeNode(blockNode));
        index += 1;
      }
      output += blockTexts.join('\n');
      continue;
    }

    output += serializeNode(node);
    index += 1;
  }

  return output;
}

function serializeNode(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;

    if (element.tagName === 'BR') {
      return '\n';
    }

    const childText = serializeNodeList(element.childNodes);

    if (BLOCK_TAG_NAMES.has(element.tagName)) {
      const blockContent = childText.replace(/\n/g, '').trim();
      if (!blockContent) {
        // Empty blocks contain only whitespace or a placeholder <br> and do
        // not contribute text; block siblings add their separators instead.
        return '';
      }
      return childText;
    }

    return childText;
  }

  return '';
}

/**
 * Removes variable-highlighting zero-width spaces and normalizes title text.
 * Multiline titles preserve intentional leading and trailing line breaks while
 * whitespace-only content becomes empty so the placeholder can reappear.
 */
export function normalizeTitleText(
  value: string,
  allowLineBreaks: boolean
): string {
  const withoutZWS = stripAngleBrackets(value).replace(/\u200B/g, '');
  if (!allowLineBreaks) {
    return withoutZWS.trim();
  }
  if (/^\s*$/.test(withoutZWS)) {
    return '';
  }
  return withoutZWS;
}

/**
 * Sets the cursor (caret) to a given character offset inside a
 * ContentEditable element by walking its text nodes.
 * The offset is in "raw" coordinates (no zero-width spaces), so ZWS
 * characters in the DOM are skipped when counting.
 */
export function setCursorOffset(element: HTMLElement, offset: number): void {
  const selection = document.getSelection();
  if (!selection) return;

  const range = document.createRange();
  let remaining = offset;

  function placeCursorIn(node: Node): boolean {
    for (const child of Array.from(node.childNodes)) {
      if (
        child instanceof HTMLElement &&
        child.hasAttribute(TITLE_VARIABLE_ATTRIBUTE)
      ) {
        const variableLength = countWithoutZWS(child.textContent ?? '');
        if (remaining === 0) {
          range.setStartBefore(child);
          return true;
        }
        if (remaining <= variableLength) {
          // Variables are atomic. Snap any requested position within a token
          // to its trailing boundary rather than placing the caret inside it.
          range.setStartAfter(child);
          return true;
        }
        remaining -= variableLength;
        continue;
      }

      if (child instanceof HTMLBRElement) {
        // A DOM <br> maps to one raw `\n` character in the title value.
        remaining -= 1;
        if (remaining === 0) {
          range.setStartAfter(child);
          return true;
        }
        continue;
      }

      if (child.nodeType === Node.ELEMENT_NODE) {
        if (placeCursorIn(child)) return true;
        continue;
      }

      if (child.nodeType !== Node.TEXT_NODE) continue;
      const text = child.textContent ?? '';
      const meaningfulLen = countWithoutZWS(text);
      if (remaining <= meaningfulLen) {
        // Map raw offset to actual DOM offset within this text node.
        // Use ch.length so surrogate pairs (e.g. emoji) count as 2 UTF-16
        // code units, matching what range.setStart expects.
        let domOffset = 0;
        let counted = 0;
        for (const ch of text) {
          if (counted >= remaining) break;
          domOffset += ch.length;
          if (ch !== ZWS) counted += ch.length;
        }
        range.setStart(child, domOffset);
        return true;
      }
      remaining -= meaningfulLen;
    }

    return false;
  }

  if (placeCursorIn(element)) {
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return;
  }

  // Offset is beyond content — place cursor at end
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function countWithoutZWS(text: string): number {
  let count = 0;
  for (const ch of text) {
    // Use ch.length so surrogate pairs (e.g. emoji) count as 2 UTF-16
    // code units, matching DOM offset conventions.
    if (ch !== ZWS) count += ch.length;
  }
  return count;
}

/**
 * Returns the plain text content of an element with zero-width spaces removed.
 * Used to read the live DOM content during editing (since EditableHeader only
 * propagates changes to the parent on blur).
 */
export function getRawTextContent(element: HTMLElement): string {
  return (element.textContent ?? '').replace(/\u200B/g, '');
}

/**
 * Gets the character offset of the current selection within a target element
 * identified by data-testid. Returns null if the selection is not within
 * the target element.
 * The returned offset is in "raw" coordinates (ZWS characters are excluded)
 * so it can be applied directly against the raw title string.
 */
export function getSelectionOffsetInElement(testId: string): number | null {
  const selection = document.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const target = document.querySelector(`[data-testid="${testId}"]`);
  if (!target || !target.contains(range.startContainer)) return null;

  // Create a range from the start of the element to the current cursor position
  const preCaretRange = document.createRange();
  preCaretRange.selectNodeContents(target);
  preCaretRange.setEnd(range.startContainer, range.startOffset);
  // Strip zero-width spaces so the offset maps to the raw title string.
  // Each DOM <br> consumes one raw `\n` character in that string.
  const fragment = preCaretRange.cloneContents();
  const lineBreakCount = fragment.querySelectorAll('br').length;
  return countWithoutZWS(preCaretRange.toString()) + lineBreakCount;
}
